"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlayFill, PiArrowRightFill, PiX } from "react-icons/pi";
import { useHomepageContext } from "../../context/HomepageContext";

export default function OvanHero({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const mainImage = content.mainImage || "/assets/2-DM0N8b4e.jpg";
  const bgShape = content.bgShape || "/assets/rounded-line-C2m8ysGi.png";
  const dotsPattern = content.dotsPattern || "/assets/dots-DP-0KuiK.svg";

  const [isDark, setIsDark] = useState(true);
  const [isPlayOpen, setIsPlayOpen] = useState(false);

  const getEmbedUrl = (url: string) => {
    if (!url) return "";
    let videoId = "";
    if (url.includes("v=")) {
      videoId = url.split("v=")[1]?.split("&")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0];
    } else if (url.includes("embed/")) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : url;
  };

  const context = useHomepageContext();
  const canEdit = context ? context.canEdit : true;

  const rawTitle = content.title || "Smarter *Marketing*\nfor *Smarter* Growth";
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState(rawTitle);

  useEffect(() => {
    setEditTitleValue(rawTitle);
  }, [rawTitle]);

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (onContentChange) {
      onContentChange({ ...content, title: editTitleValue });
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTitleBlur();
    }
    if (e.key === "Escape") {
      setEditTitleValue(rawTitle);
      setIsEditingTitle(false);
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");

      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };

      window.addEventListener("ovan-theme-change", handleThemeChange);
      return () => {
        window.removeEventListener("ovan-theme-change", handleThemeChange);
      };
    }
  }, []);

  return (
    <div
      id="hero"
      className={`relative w-full pb-6 transition-colors duration-200 ${isDark ? "bg-slate-900" : "bg-white"
        } ${isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
        }`}
      style={{ fontFamily }}
    >
      {/* Top Banner Section */}
      <section className={`relative overflow-hidden w-full md:pt-14 md:pb-32 pt-10 pb-20 transition-colors duration-200 rounded-b-[80px] ${isDark ? "bg-slate-800" : "bg-slate-50"
        }`}>
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <div className="mb-4">
            <span className="py-1.5 px-3 rounded-xl inline-flex bg-blue-600/5 shadow-sm shadow-blue-600/10 text-blue-600 font-semibold uppercase text-xs">
              <EditableText
                value={content.tag || "Digital Marketing"}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, tag: val })
                }
                as="span"
                placeholder="Tag"
              />
            </span>
          </div>

          {/* Heading - matching reference font-weights and line break */}
          <h4 className={`font-medium lg:leading-normal leading-normal text-4xl lg:text-5xl mb-5 w-full ${isDark ? "text-white" : "text-slate-900"}`}>
            {isEditingTitle && canEdit ? (
              <textarea
                value={editTitleValue}
                onChange={(e) => setEditTitleValue(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyDown={handleTitleKeyDown}
                autoFocus
                className={`w-full bg-transparent border-b-2 border-blue-400 outline-none text-center resize-y text-4xl lg:text-5xl font-medium ${isDark ? "text-white" : "text-slate-900"}`}
                rows={2}
              />
            ) : (
              <span
                onClick={() => {
                  if (canEdit) {
                    setIsEditingTitle(true);
                  }
                }}
                className={canEdit ? "cursor-text outline-none rounded transition-all px-1 -mx-1" : ""}
              >
                {(() => {
                  const titleToParse = rawTitle === "Smarter Marketing for Smarter Growth"
                    ? "Smarter *Marketing*\nfor *Smarter* Growth"
                    : rawTitle;

                  const lines = titleToParse.split(/\r?\n|<br\s*\/?>/i);
                  return lines.map((line: string, lineIdx: number) => {
                    const parts = line.split(/\*(.*?)\*/g);
                    return (
                      <React.Fragment key={lineIdx}>
                        {lineIdx > 0 && <br />}
                        {parts.map((part: string, partIdx: number) => {
                          if (partIdx % 2 === 1) {
                            return (
                              <span key={partIdx} className={`font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>
                                {part}
                              </span>
                            );
                          }
                          return (
                            <span key={partIdx} className={isDark ? "text-white" : "text-slate-700"}>
                              {part}
                            </span>
                          );
                        })}
                      </React.Fragment>
                    );
                  });
                })()}
              </span>
            )}
          </h4>

          {/* Description - text color dynamically updates */}
          <p className={`text-base md:text-lg mb-8 max-w-2xl mx-auto transition-colors leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"
            }`}>
            <EditableText
              value={
                content.text ||
                "Our design projects are fresh and simple and will benefit your business greatly. Learn more about our work!"
              }
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, text: val })
              }
              as="span"
              className={`${isDark ? "text-slate-300" : "text-slate-600"}`}
              placeholder="Description"
            />
          </p>

          {/* Spiral Decorator nested inside the container */}
          <img
            src={bgShape}
            alt="shape decorator"
            className="absolute rotate-45 top-20 -left-6 md:-left-32 -z-10 opacity-25"
          />
        </div>
      </section>

      {/* Overlapping Video/Image Card */}
      <section className="relative md:py-16 py-12 -mt-40 sm:-mt-35 -mt-22 z-10">
        <div className="max-w-4xl mx-auto px-4 relative">
          <div className={`relative rounded-2xl overflow-hidden shadow-xl border transition-colors ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-100"
            }`}>
            <img
              src={mainImage}
              alt="Marketing Showcase"
              className="w-full object-cover animate-fade-in"
            />
            {/* Play Button Overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/25 transition-all duration-300">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsPlayOpen(true);
                }}
                className="size-16 sm:size-20 bg-white/50 hover:bg-white text-blue-600 hover:text-blue-700 border-4 border-white/30 text-2xl hover:text-3xl duration-500 inline-flex justify-center items-center rounded-full shadow-lg"
              >
                <PiPlayFill className="size-8" />
              </button>
            </div>
          </div>

          {/* Overlapping Button Drawer Card at the TOP border */}
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-[270px] py-3 px-4 rounded-full bg-slate-50 dark:bg-slate-800 shadow-sm shadow-slate-200 dark:shadow-slate-700 flex items-center justify-between z-30">
            <a
              href="/contactus"
              className="h-8 px-5 tracking-wider inline-flex justify-center items-center text-xs uppercase font-bold rounded-full bg-blue-600/10 hover:bg-blue-600 text-blue-600 hover:text-white transition-all duration-250"
            >
              <EditableText
                value={content.primaryBtnText || "Get Started"}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, primaryBtnText: val })
                }
                as="span"
                placeholder="Primary Button"
              />
            </a>
            <a
              href="/aboutus"
              className="hover:text-blue-600 font-bold uppercase tracking-wider text-xs ms-2 flex items-center transition-colors text-slate-700 dark:text-slate-300"
            >
              <EditableText
                value={content.secondaryBtnText || "Learn More"}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, secondaryBtnText: val })
                }
                as="span"
                placeholder="Secondary Button"
              />
              <PiArrowRightFill className="ms-0.5 size-3.5" />
            </a>
          </div>

          {/* Dots Ornament positioned perfectly relative to the frame */}
          <span className="absolute -top-12 -left-12 -z-10 opacity-80 pointer-events-none">
            <img src={dotsPattern} alt="dots decorator" className="size-28" />
          </span>
        </div>
      </section>

      {/* Video Modal Overlay */}
      {isPlayOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-4xl aspect-video rounded-lg overflow-hidden bg-black shadow-2xl">
            {/* Close Button */}
            <button
              onClick={() => setIsPlayOpen(false)}
              className="absolute top-4 right-4 z-[10000] p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors focus:outline-none"
            >
              <PiX className="w-6 h-6" />
            </button>
            <iframe
              src={getEmbedUrl(content.videoLink || "https://www.youtube.com/watch?v=7ahgosTZJHg")}
              className="w-full h-full border-none"
              allow="autoplay; encrypted-media"
              allowFullScreen
              title="Hero Showcase Video"
            />
          </div>
        </div>
      )}
    </div>
  );
}
